import React from 'react';
import { getThemeClasses } from '../utils/themeUtils';

/**
 * Comprehensive Loading Indicator Component
 * Provides various loading states and indicators for different operations
 */
const LoadingIndicator = ({
  type = 'spinner', // 'spinner', 'dots', 'pulse', 'progress', 'skeleton'
  size = 'medium', // 'small', 'medium', 'large'
  message = '',
  progress = null, // For progress type: 0-100
  overlay = false, // Show as overlay
  className='',
  color = 'primary' // 'primary', 'secondary', 'white'
}) => {
  const theme = getThemeClasses();

  const getSizeClasses = () => {
    const sizes = {
      small: {
        spinner: 'w-4 h-4',
        dots: 'w-6 h-6',
        text: 'text-xs'
      },
      medium: {
        spinner: 'w-6 h-6',
        dots: 'w-8 h-8',
        text: 'text-sm'
      },
      large: {
        spinner: 'w-8 h-8',
        dots: 'w-12 h-12',
        text: 'text-base'
      }
    };
    return sizes[size] || sizes.medium;
  };

  const getColorClasses = () => {
    const colors = {
      primary: 'text-teal-600 dark:text-teal-400',
      secondary: 'text-gray-600 dark:text-gray-400',
      white: 'text-white'
    };
    return colors[color] || colors.primary;
  };

  const sizeClasses = getSizeClasses();
  const colorClasses = getColorClasses();

  const renderSpinner = () => (
    <svg 
      className={`animate-spin ${sizeClasses.spinner} ${colorClasses}`} 
      fill="none" 
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle 
        className="opacity-25" 
        cx="12" 
        cy="12" 
        r="10" 
        stroke="currentColor" 
        strokeWidth="4"
      />
      <path 
        className="opacity-75" 
        fill="currentColor" 
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  const renderDots = () => (
    <div className={`flex space-x-1 ${sizeClasses.dots}`}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={`w-2 h-2 rounded-full ${colorClasses} animate-pulse`}
          style={{
            animationDelay: `${i * 0.2}s`,
            animationDuration: '1s'
          }}
        />
      ))}
    </div>
  );

  const renderPulse = () => (
    <div className={`${sizeClasses.spinner} ${colorClasses}`}>
      <div className="w-full h-full rounded-full animate-ping opacity-75" />
    </div>
  );

  const renderProgress = () => (
    <div className="w-full max-w-xs">
      <div className={`w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2`}>
        <div 
          className="bg-teal-600 h-2 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${Math.min(100, Math.max(0, progress || 0))}%` }}
        />
      </div>
      {progress !== null && (
        <div className={`text-center mt-2 ${sizeClasses.text} ${theme.text.secondary}`}>
          {Math.round(progress)}%
        </div>
      )}
    </div>
  );

  const renderSkeleton = () => (
    <div className="animate-pulse space-y-3">
      <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4" />
      <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2" />
      <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-5/6" />
    </div>
  );

  const renderLoadingContent = () => {
    switch (type) {
      case 'dots':
        return renderDots();
      case 'pulse':
        return renderPulse();
      case 'progress':
        return renderProgress();
      case 'skeleton':
        return renderSkeleton();
      case 'spinner':
      default:
        return renderSpinner();
    }
  };

  const content = (
    <div className={`flex flex-col items-center justify-center space-y-3 ${className}`}>
      {renderLoadingContent()}
      {message && (
        <div className={`${sizeClasses.text} ${theme.text.secondary} text-center max-w-xs`}>
          {message}
        </div>
      )}
    </div>
  );

  if (overlay) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        role="status"
        aria-live="polite"
        aria-label={message || 'Loading'}
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-xl">
          {content}
        </div>
      </div>
    );
  }

  return (
    <div 
      role="status" 
      aria-live="polite" 
      aria-label={message || 'Loading'}
      className={className}
    >
      {content}
    </div>
  );
};

/**
 * Inline Loading Spinner for buttons and small spaces
 */
export const InlineLoader = ({ 
  size = 'small', 
  color = 'white',
  className='' 
}) => (
  <LoadingIndicator 
    type="spinner" 
    size={size} 
    color={color} 
    className={className}
  />
);

/**
 * Button Loading State Component
 */
export const ButtonLoader = ({ 
  loading = false, 
  children, 
  loadingText = 'Loading...',
  className='',
  ...buttonProps 
}) => (
  <button 
    {...buttonProps}
    disabled={loading || buttonProps.disabled}
    className={`relative ${className}`}
  >
    {loading && (
      <div className="absolute inset-0 flex items-center justify-center">
        <InlineLoader size="small" color="white" />
      </div>
    )}
    <span className={loading ? 'opacity-0' : 'opacity-100'}>
      {loading ? loadingText : children}
    </span>
  </button>
);

/**
 * File Upload Progress Component
 */
export const FileUploadProgress = ({ 
  progress = 0, 
  fileName = '', 
  status = 'uploading' // 'uploading', 'processing', 'complete', 'error'
}) => {
  const theme = getThemeClasses();
  
  const getStatusIcon = () => {
    switch (status) {
      case 'complete':
        return (
          <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      default:
        return <InlineLoader size="small" color="primary" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'processing':
        return 'Processing...';
      case 'complete':
        return 'Complete';
      case 'error':
        return 'Failed';
      default:
        return 'Uploading...';
    }
  };

  return (
    <div className={`p-4 border rounded-lg ${theme.border.primary} ${theme.bg.secondary}`}>
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          {getStatusIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${theme.text.primary}`}>
            {fileName}
          </p>
          <p className={`text-xs ${theme.text.secondary}`}>
            {getStatusText()}
          </p>
        </div>
        <div className="flex-shrink-0 text-sm font-medium">
          {status === 'uploading' && `${Math.round(progress)}%`}
        </div>
      </div>
      
      {(status === 'uploading' || status === 'processing') && (
        <div className="mt-3">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                status === 'processing' 
                  ? 'bg-blue-600 animate-pulse' 
                  : 'bg-teal-600'
              }`}
              style={{ 
                width: status === 'processing' ? '100%' : `${Math.min(100, Math.max(0, progress))}%` 
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Operation Status Component
 */
export const OperationStatus = ({ 
  operation = '', 
  status = 'pending', // 'pending', 'running', 'success', 'error'
  message = '',
  progress = null,
  onRetry = null,
  onCancel = null
}) => {
  const theme = getThemeClasses();

  const getStatusConfig = () => {
    const configs = {
      pending: {
        icon: <InlineLoader size="small" color="secondary" />,
        color: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-100 dark:bg-gray-700'
      },
      running: {
        icon: <InlineLoader size="small" color="primary" />,
        color: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30'
      },
      success: {
        icon: (
          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        ),
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900/30'
      },
      error: {
        icon: (
          <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        ),
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-100 dark:bg-red-900/30'
      }
    };
    return configs[status] || configs.pending;
  };

  const statusConfig = getStatusConfig();

  return (
    <div className={`p-3 rounded-lg ${statusConfig.bgColor}`}>
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          {statusConfig.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${statusConfig.color}`}>
            {operation}
          </p>
          {message && (
            <p className={`text-xs mt-1 ${theme.text.secondary}`}>
              {message}
            </p>
          )}
        </div>
        
        {/* Action buttons */}
        {(onRetry || onCancel) && (
          <div className="flex-shrink-0 flex space-x-2">
            {onRetry && status === 'error' && (
              <button
                onClick={onRetry}
                className="text-xs px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded transition-colors dark:bg-gray-800"
              >
                Retry
              </button>
            )}
            {onCancel && (status === 'pending' || status === 'running') && (
              <button
                onClick={onCancel}
                className="text-xs px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded transition-colors dark:bg-gray-800"
              >
                Cancel
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* Progress bar for running operations */}
      {status === 'running' && progress !== null && (
        <div className="mt-2">
          <LoadingIndicator type="progress" progress={progress} />
        </div>
      )}
    </div>
  );
};

export default LoadingIndicator;
